import React, { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import {
  AssessmentDetail,
  AssessmentsAPI,
  ReviewParams,
} from '@/services/assessmentsAPI';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { Heading, Body } from '@/components/Typography';

interface ReviewFormProps {
  assessment: AssessmentDetail;
  onSuccess: () => void;
  onCancel: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  assessment,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ReviewParams>({
    reviewed_by_id: 1, // TODO: Get from auth context
    final_status: 'approved',
    notes: '',
    final_score: undefined,
    override: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await AssessmentsAPI.reviewAssessment(assessment.external_id, formData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to submit review');
      console.error('Error submitting review:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof ReviewParams,
    value: string | number | boolean | undefined,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Stack vertical gap={16}>
          <Heading>Submit Review</Heading>

          {error && (
            <Alert
              status="danger"
              icon={faExclamationTriangle}
              description={error}
            />
          )}

          <Stack vertical gap={4}>
            <Body weight="medium">Final Status *</Body>
            <select
              value={formData.final_status}
              onChange={(e) => handleChange('final_status', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
            >
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending_review">Pending Review</option>
            </select>
          </Stack>

          <Stack vertical gap={4}>
            <Body weight="medium">Final Score (0-100)</Body>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.final_score || ''}
              onChange={(e) =>
                handleChange(
                  'final_score',
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              placeholder="Optional"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
            />
          </Stack>

          <Stack vertical gap={4}>
            <Body weight="medium">Notes</Body>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={4}
              placeholder="Add any notes about this assessment..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </Stack>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={formData.override}
              onChange={(e) => handleChange('override', e.target.checked)}
            />
            <Body>Override automatic score</Body>
          </label>

          <Stack gap={16} justify="end">
            <Button
              type="button"
              onClick={onCancel}
              a11yLabel="Cancel review submission"
              label="Cancel"
              disabled={submitting}
            />
            <Button
              type="submit"
              a11yLabel="Submit review"
              label={submitting ? 'Submitting...' : 'Submit Review'}
              disabled={submitting}
            />
          </Stack>
        </Stack>
      </form>
    </Card>
  );
};

export default ReviewForm;
